import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import generateId from './generateId';
import { updateHashProps, rewriteUserId } from '../general';
import { addDatasToSave, loadDbModel } from './workBackendSlice';
import { saveAllData, saveData, deleteData, SignUpPayload } from './dataBackend';

export type MemoCardContentDb = {
  cardId: string;
  content: string;
  goodsId?: string;
  version?: number;
}
const initial: MemoCardContentDb = {
  cardId: '',
  content: '',
  goodsId: undefined,
  version: undefined
};

export type MemoCardContentDbHashType = {
  [key in string]: MemoCardContentDb;
}

export type MemoCardContentAddPayload = {
  id?: string;
} & MemoCardContentDb;

export type MemoCardContentUpdatePayload = {
  id: string;
} & { [P in keyof MemoCardContentDb]?: MemoCardContentDb[P] };

export type MemoCardContentDbState = {
  memoCardContents: MemoCardContentDbHashType;
}

export const generateMemoCardContentId = (): string => generateId('mcc');
let initialState: MemoCardContentDbState = { memoCardContents: {} };
const key = 'memoCardContents';

const memoCardContentDbSlice = createSlice({
  name: 'memoCardContentDb',
  initialState,
  reducers: {
    initializeMemoCardContents(state: MemoCardContentDbState) {
      state.memoCardContents = {};
    },
    addMemoCardContent(state: MemoCardContentDbState, action: PayloadAction<MemoCardContentAddPayload>) {
      let { id, ...obj } = action.payload;
      if (!id) id = generateMemoCardContentId();
      state.memoCardContents[id] = obj;
      saveData(key, id, obj);
    },
    updateMemoCardContent(state: MemoCardContentDbState, action: PayloadAction<MemoCardContentUpdatePayload>) {
      const { id, ...obj } = action.payload;
      const memoCardContent = state.memoCardContents[id];
      if (memoCardContent === undefined) {
        console.log(`*** MemoCardContent not found: ${id}`);
      } else {
        const newData = { ...state.memoCardContents[id], ...obj };
        state.memoCardContents[id] = newData;
        saveData(key, id, newData);
      }
    },
    deleteMemoCardContent(state: MemoCardContentDbState, action: PayloadAction<MemoCardContentUpdatePayload>) {
      const id = action.payload.id;
      delete state.memoCardContents[id];
      deleteData(key, id);
    },
    saveMemoCardContents(state: MemoCardContentDbState, action: PayloadAction<void>) {
      saveAllData(key, state.memoCardContents);
    },
    signUpMemoCardContents(state: MemoCardContentDbState, action: PayloadAction<SignUpPayload>) {
      const values = rewriteUserId(state.memoCardContents, action.payload);
      addDatasToSave(key, values);
    }
  },
  extraReducers: (builder) => {
    builder.addCase(loadDbModel(key).fulfilled, (state: MemoCardContentDbState, action) => {
      const values = action.payload;
      const [changed, updatedValues] = updateHashProps(initial, values);
      state.memoCardContents = updatedValues;
      if (changed) saveAllData(key, state.memoCardContents);
    })
  }
})

export const { initializeMemoCardContents, addMemoCardContent, updateMemoCardContent, deleteMemoCardContent, saveMemoCardContents, signUpMemoCardContents } = memoCardContentDbSlice.actions;
export default memoCardContentDbSlice.reducer;
